import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ArrowsClockwise, PencilSimple} from '../../icons';
import {Spinner} from '../../Spinner';
import {formatInputNumber} from './state/economiaModel';
import {FIELD_EDITED, FIELD_KEPT, FIELD_MISSING, FIELD_SYNCED} from './state/escalafyProvenance';

// Los datos del negocio en el orden del mockup 2, con su unidad y su ayuda. El margen es el único
// obligatorio (lleva el asterisco y el «¿Cómo calculo el margen?»).
const BUSINESS_FIELDS = [
    {field: 'gross_margin_pct', unit: 'pct', required: true},
    {field: 'avg_ticket', unit: 'money', hintKey: 'economics.form.hint_ticket'},
    {field: 'shipping_per_order', unit: 'money', hintKey: 'economics.form.hint_shipping'},
    {field: 'payment_fee_pct', unit: 'pct', hintKey: 'economics.form.hint_fee'},
    {field: 'fixed_costs_monthly', unit: 'money', hintKey: 'economics.form.hint_fixed'},
    {field: 'agency_fee_monthly', unit: 'money', hintKey: 'economics.form.hint_fee_agency'},
];

const FIELD_CLASS = {[FIELD_SYNCED]: ' -synced', [FIELD_EDITED]: ' -edited'};

/**
 * El formulario de los cinco datos + vigencia, moneda y nota. CONTROLADO: los valores viven en quien
 * lo monta, porque «Con lo que cargaste, ya tienes» se recalcula mientras se tipea y vive al lado.
 *
 * Lo usan el perfil del cliente y el override de una línea (`idPrefix` distinto para que los ids de
 * los campos no se repitan si los dos están abiertos a la vez). Los números se escriben como texto
 * (`inputMode="decimal"`): un `type="number"` no acepta «38.000» ni «10,5», que es como se escribe
 * plata en Argentina.
 *
 * Con Escalafy vinculado, el perfil del cliente pasa `banner` (la franja de la conexión),
 * `fieldSources` (de dónde sale cada dato, `escalafyFieldSources`) y `saveNotice` (guardar a mano
 * deja a Escalafy sin pisar el perfil): sin ellos, el formulario es el de siempre.
 */
export function EconomiaForm({
    idPrefix = 'eco',
    values,
    errors = {},
    currencies = [],
    onChange,
    onSubmit,
    onCancel,
    saving = false,
    showError = false,
    sameDateNotice = false,
    showNote = true,
    banner = null,
    fieldSources = {},
    saveNotice = null,
}) {
    const {t} = useTranslation();
    const [helpOpen, setHelpOpen] = useState(false);
    const currency = values.currency || '';

    const change = (field) => (event) => onChange(field, event.target.value);

    const formatValue = (unit, value) => {
        const number = formatInputNumber(value);
        if (unit === 'pct') return `${number} %`;
        return currency ? `${currency} ${number}` : number;
    };

    const renderSourceTag = (source) => {
        if (!source) return null;
        if (source.kind === FIELD_SYNCED) {
            return (
                <span className="eco-field__src">
                    <ArrowsClockwise size={10} weight="bold" aria-hidden="true"/>
                    {t('economics.escalafy.field.tag')}
                </span>
            );
        }
        if (source.kind === FIELD_EDITED) {
            return (
                <span className="eco-field__src -edited">
                    <PencilSimple size={10} weight="bold" aria-hidden="true"/>
                    {t('economics.escalafy.field.tag_edited')}
                </span>
            );
        }
        return null;
    };

    // La ayuda del campo dice de dónde sale el número cuando no lo cargó el usuario: el usuario tiene
    // que poder creerle (o corregirlo) sin ir a Escalafy a ver la fórmula.
    const renderHint = ({field, unit, hintKey}, source, hintId) => {
        if (source && source.kind === FIELD_SYNCED) {
            return (
                <span className="eco-field__hint -src" id={hintId}>
                    <strong>{t('economics.escalafy.field.from_label')}</strong>{' '}
                    {t(`economics.escalafy.field.from.${field}`)}
                </span>
            );
        }
        if (source && source.kind === FIELD_EDITED) {
            return (
                <span className="eco-field__hint -edited" id={hintId}>
                    {t('economics.escalafy.field.edited', {
                        value: formatValue(unit, source.value), interpolation: {escapeValue: false},
                    })}{' '}
                    <button type="button" className="eco-link" data-testid={`${idPrefix}-${field}-restore`}
                            onClick={() => onChange(field, formatInputNumber(source.value))}>
                        {t('economics.escalafy.field.restore')}
                    </button>
                </span>
            );
        }
        if (source && source.kind === FIELD_KEPT) {
            return <span className="eco-field__hint" id={hintId}>{t('economics.escalafy.field.kept')}</span>;
        }
        if (source && source.kind === FIELD_MISSING) {
            return (
                <span className="eco-field__hint" id={hintId}>
                    {source.reason
                        ? `${t(`client_panel.economy.escalafy.warning.${source.reason}`)} ${t('economics.escalafy.field.missing_cta')}`
                        : t('economics.escalafy.field.missing')}
                </span>
            );
        }
        return hintKey ? <span className="eco-field__hint" id={hintId}>{t(hintKey)}</span> : null;
    };

    const renderField = (spec) => {
        const {field, unit, required} = spec;
        const id = `${idPrefix}-${field}`;
        const invalid = !!errors[field];
        const source = fieldSources[field] || null;
        const hint = renderHint(spec, source, `${id}-hint`);
        return (
            <div className={`eco-field${invalid ? ' -invalid' : ''}${(source && FIELD_CLASS[source.kind]) || ''}`}
                 key={field} data-source={source ? source.kind : undefined}>
                <div className="eco-field__top">
                    <label className="eco-field__label" htmlFor={id}>
                        {t(`client_panel.economy.input.${field}`)}
                        {required && <span className="eco-field__req" aria-hidden="true">*</span>}
                    </label>
                    {renderSourceTag(source)}
                </div>
                <div className="eco-input">
                    {unit === 'money' && currency && <span className="eco-input__unit">{currency}</span>}
                    <input
                        id={id}
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        value={values[field] || ''}
                        onChange={change(field)}
                        aria-invalid={invalid}
                        aria-required={required ? true : undefined}
                        aria-describedby={hint ? `${id}-hint` : undefined}
                    />
                    {unit === 'pct' && <span className="eco-input__unit">%</span>}
                </div>
                {hint}
            </div>
        );
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!saving) onSubmit();
    };

    return (
        <form className="eco-form" onSubmit={handleSubmit} noValidate data-testid={`${idPrefix}-form`}>
            <section className="eco-fs" aria-labelledby={`${idPrefix}-business-title`}>
                <div className="eco-fs__head">
                    <h2 className="eco-fs__title" id={`${idPrefix}-business-title`}>
                        {t('economics.form.section_business')}
                    </h2>
                    <button type="button" className="eco-link" aria-expanded={helpOpen}
                            onClick={() => setHelpOpen((open) => !open)}>
                        {t('economics.form.help_margin')}
                    </button>
                </div>
                {banner}
                {helpOpen && <p className="eco-fs__help">{t('economics.form.help_margin_body')}</p>}
                <div className="eco-grid2">
                    {BUSINESS_FIELDS.map(renderField)}
                </div>
            </section>

            <section className="eco-fs" aria-labelledby={`${idPrefix}-validity-title`}>
                <div className="eco-fs__head">
                    <h2 className="eco-fs__title" id={`${idPrefix}-validity-title`}>
                        {t('economics.form.section_validity')}
                    </h2>
                </div>
                <div className="eco-grid2">
                    <div className={`eco-field${errors.effective_from ? ' -invalid' : ''}`}>
                        <label className="eco-field__label" htmlFor={`${idPrefix}-effective_from`}>
                            {t('economics.form.effective_from')}
                        </label>
                        <div className="eco-input">
                            <input id={`${idPrefix}-effective_from`} type="date"
                                   value={values.effective_from || ''}
                                   onChange={change('effective_from')}
                                   aria-invalid={!!errors.effective_from}/>
                        </div>
                    </div>
                    <div className={`eco-field${errors.currency ? ' -invalid' : ''}`}>
                        <label className="eco-field__label" htmlFor={`${idPrefix}-currency`}>
                            {t('economics.form.currency')}
                        </label>
                        <div className="eco-input -select">
                            <select id={`${idPrefix}-currency`} value={currency}
                                    onChange={change('currency')} aria-invalid={!!errors.currency}>
                                {!currency && <option value="">—</option>}
                                {currencies.map((code) => <option key={code} value={code}>{code}</option>)}
                            </select>
                        </div>
                    </div>
                    {showNote && (
                        <div className="eco-field -wide">
                            <label className="eco-field__label" htmlFor={`${idPrefix}-note`}>
                                {t('economics.form.note')}
                            </label>
                            <textarea id={`${idPrefix}-note`} rows={2} value={values.note || ''}
                                      placeholder={t('economics.form.note_placeholder')}
                                      onChange={change('note')}/>
                        </div>
                    )}
                </div>
                {sameDateNotice && (
                    <p className="eco-fs__notice" data-testid={`${idPrefix}-same-date`}>
                        {t('economics.form.same_date_notice')}
                    </p>
                )}
            </section>

            {showError && <p className="eco-form__error" role="alert">{t('economics.form.error')}</p>}

            {saveNotice && (
                <p className="eco-form__notice" data-testid={`${idPrefix}-save-notice`}>
                    <ArrowsClockwise size={13} weight="bold" aria-hidden="true"/>
                    {saveNotice}
                </p>
            )}

            <div className="eco-form__acts">
                {onCancel && (
                    <button type="button" className="pnl-btn pnl-btn--ghost" onClick={onCancel} disabled={saving}>
                        {t('economics.form.cancel')}
                    </button>
                )}
                <button type="submit" className="pnl-btn pnl-btn--primary" disabled={saving}
                        aria-busy={saving || undefined} data-testid={`${idPrefix}-save`}>
                    {saving && <Spinner color="negative very-small"/>}
                    {t('economics.form.save')}
                </button>
            </div>
        </form>
    );
}

export default EconomiaForm;
